import { useLocation } from '@solidjs/router';
import { createEffect, createSignal, Match, mergeProps, Show, Switch, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { addMyContent, deleteMyContentItemByPermalink, getMyContent, MyContentItem } from '../my-content/my-content-helpers';
import { HeartCviIcon } from '../icons-library/heart-cvi';
import { HeartFullCviIcon } from '../icons-library/heart-full-cvi';
import { linkItemClass } from '../share-and-save/share-and-save';

type MyContentHandlerProps =
| {
    action: 'add';
    title?: string;
    type: string;
    permalink?: string;
    onlyIcon?: boolean;
}
| {
    action: 'remove';
    permalink: string;
};

const defaultProps = {
    title: '',
    type: '',
    onlyIcon: false,
};

export const MyContentHandler = (componentProps: MyContentHandlerProps) => {
    const props = mergeProps(defaultProps, componentProps);
    const { localize, siteInfo, userProfileUrls } = useContext(AppContext);
    const [existsInMyContent, setExistsInMyContent] = createSignal(false);
    const location = useLocation();

    createEffect(() => {
        checkIfItemExistsInMyContent();
    });

    const featureEnabled = () => siteInfo.siteType === 'default';
    const isOnBookmarksPage = () => location.pathname === userProfileUrls.bookmarks;

    const handleOnClickSaveToMyContent = (e: any) => {
        e.preventDefault();
        if (props.action === 'remove') {
            return;
        }

        const permalink = props.permalink === '' ? '/' : props.permalink;

        if (existsInMyContent()) {
            deleteMyContentItemByPermalink(permalink);
            setExistsInMyContent(false);
            return;
        }

        const myContentObjectToSave: MyContentItem = {
            title: props.title,
            permalink,
            type: props.type,
        };

        addMyContent(myContentObjectToSave);
        setExistsInMyContent(true);
    };

    const checkIfItemExistsInMyContent = () => {
        const myContent: MyContentItem[] | [] = getMyContent();

        const exists = myContent.find((item: any) => {
            return item.permalink === props.permalink;
        });

        if (exists) {
            setExistsInMyContent(true);
        } else {
            setExistsInMyContent(false);
        }
    };

    return (
        <Switch>
            <Match when={props.action === 'add'}>
                <Show when={featureEnabled() && !isOnBookmarksPage()}>
                    <button class={linkItemClass} onClick={handleOnClickSaveToMyContent} aria-label={localize('save-to-my-content', 'Save to my content')}>
                        <Show
                            when={existsInMyContent()}
                            fallback={
                                <HeartCviIcon size="lg" ariaLabel={localize('my_content_star_icon', 'Add to My Content')} />
                            }
                        >
                            <HeartFullCviIcon size="lg" ariaLabel={localize('my_content_remove_star', 'Remove from My Content')} />
                        </Show>
                        <Show when={props.type === 'page' || !props.onlyIcon}>
                            <span class="body-md font-cvi-regular">
                                {localize('save-to-my-content', 'Save to my content')}
                            </span>
                        </Show>
                    </button>
                </Show>
            </Match>
            <Match when={props.action === 'remove'}>
                <Show when={featureEnabled() && isOnBookmarksPage()}>
                    <button
                        class="bg-transparent m-8 border-none cursor-pointer [&>svg:hover_path]:fill-primary-hover"
                        onClick={() => {
                            deleteMyContentItemByPermalink(props.permalink);
                            setExistsInMyContent(false);
                        }}
                        aria-label={localize('my_content_remove_star', 'Remove from My Content')}
                    >
                        <HeartFullCviIcon size="lg" ariaLabel={localize('my_content_remove_star', 'Remove from My Content')} />
                    </button>
                </Show>
            </Match>
        </Switch>
    );
};
